import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin } from "lucide-react";
import { toast } from "sonner";

export const SHIPMENT_OPTS = [
  { key: "at_origin", label: "At Origin" },
  { key: "in_transit", label: "In Transit" },
  { key: "on_the_way", label: "On the way" },
  { key: "at_destination", label: "At Destination" },
  { key: "delivered", label: "Delivered" },
];

export const shipmentLabel = (k) => (SHIPMENT_OPTS.find((s) => s.key === k) || {}).label || "At Origin";

// lead: { id, order_number, shipment_status, eta, current_location | current_location_text }
export function ShipmentStatusDialog({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState("at_origin");
  const [loc, setLoc] = useState("");
  const [eta, setEta] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lead) {
      setStatus(lead.shipment_status || "at_origin");
      setLoc(lead.current_location ?? lead.current_location_text ?? "");
      setEta(lead.eta || "");
    }
  }, [lead]);

  const save = async () => {
    if (!lead) return;
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/shipment-status`, { shipment_status: status, eta: eta || null, current_location: loc });
      toast.success("Status updated — customer tracking refreshed");
      onSaved && onSaved({ shipment_status: status, eta, current_location: loc });
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none" data-testid="shipment-status-dialog">
        <DialogHeader><DialogTitle>Update Status — {lead?.order_number}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Current Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-none mt-1" data-testid="shipment-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SHIPMENT_OPTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> Current Location</label>
            <Input className="rounded-none mt-1" placeholder="e.g. Near Nashik toll plaza / Warehouse — Pune" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="shipment-location-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">ETA (optional)</label>
            <Input className="rounded-none mt-1" placeholder="e.g. Tomorrow 4 PM / 12 Jun 2026" value={eta} onChange={(e) => setEta(e.target.value)} data-testid="shipment-eta-input" />
          </div>
          <p className="text-[11px] text-muted-foreground">Saving updates the customer's live tracking link instantly.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="shipment-status-save">{busy ? "Saving…" : "Save & Refresh"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
